import { motion } from "framer-motion";
import { Brain, Zap, Cpu, Activity, TrendingUp, Lightbulb } from "lucide-react";

export default function EmergingAISection (){
    
    // Informative list to add professional content to the left column
    const focusAreas = [
        { icon: Cpu, text: 'Custom LLM Implementation (OpenAI, Gemini)' },
        { icon: Activity, text: 'MLOps & CI/CD for AI Models' },
        { icon: TrendingUp, text: 'Predictive Analytics & Forecasting' },
        { icon: Lightbulb, text: 'AI-Driven Process Automation (RPA)' },
    ];

    return (
        <section id="ai" className="py-24 bg-gray-900 relative overflow-hidden">
            
            {/* Background Ambient Glow */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20">
                <div className="absolute top-[-10%] left-1/4 w-[600px] h-[600px] bg-sky-500/10 rounded-full blur-[150px] animate-none" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    
                    {/* LEFT COLUMN: Text and Informative List */}
                    <motion.div
                        className="lg:pr-8"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-sm font-semibold uppercase text-sky-400 tracking-wider">Innovation & R&D</span>
                        <h2 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
                            Emerging Tech & <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-sky-300">AI Strategy</span>
                        </h2>
                        <p className="mt-6 text-lg text-gray-300 max-w-xl">
                            Beyond standard IT, my expertise includes <strong className="bg-blue-300 text-gray-900 px-1 rounded">
    Research & Development
  </strong> in AI-driven solutions. I help businesses strategically integrate cutting-edge technologies to achieve a competitive edge and operational excellence.
                        </p>

                        <div className="mt-8 space-y-4">
                            {focusAreas.map((item, index) => (
                                <motion.div
                                    key={index}
                                    className="flex items-center p-3 rounded-lg bg-gray-800/50 border border-gray-700/50 shadow-md"
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, amount: 0.5 }}
                                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                                >
                                    <item.icon className="w-5 h-5 text-indigo-400 mr-3 flex-shrink-0" />
                                    <span className="text-gray-300 text-sm">{item.text}</span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                    
                    {/* RIGHT COLUMN: Modern AI Neural Core Visual */}
                    <motion.div
                        className="flex justify-center relative min-h-[450px]"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.8, type: 'spring', stiffness: 50 }}
                    >
                        {/* Outer Glassmorphic Container (The Frame) */}
                        <div className="absolute inset-0 w-full h-full max-w-[450px] max-h-[450px] mx-auto bg-gray-800/70 rounded-3xl backdrop-blur-lg border border-indigo-600/30 shadow-2xl shadow-indigo-900/40 flex items-center justify-center">
                            
                            {/* Inner Pulsing Core (The Brain) */}
                            <div className="relative w-72 h-72">
                                {/* Base Blur Glow (Simulates Energy) */}
                                <div className="absolute inset-0 bg-sky-500/10 rounded-full blur-3xl opacity-70 animate-pulse [animation-duration:4s]"></div>
                                
                                {/* Central Brain Icon */}
                                <div className="absolute inset-4 bg-gray-900/50 rounded-full flex items-center justify-center">
                                    <Brain className="w-40 h-40 text-white opacity-80" style={{ filter: 'drop-shadow(0 0 15px rgba(129, 140, 248, 0.7))' }} />
                                </div>
                                
                                {/* Connection Lines (Simulates Data Flow) */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <svg viewBox="0 0 100 100" className="w-full h-full opacity-30 animate-spin-slow">
                                        <circle cx="50" cy="50" r="45" stroke="#6366F1" strokeWidth="0.5" fill="none" />
                                        <path d="M50 5 a45 45 0 0 1 45 45" stroke="#38BDF8" strokeWidth="1" fill="none" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                </div>
            </div>
        </section>
    );
}